import React, { useEffect, useState } from 'react'

import {
  ActionFlag,
  IActionRepeaterValue,
  isValidUrl,
  TypographyGroupValue,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import {
  cn,
  ColorVariant,
  ColumnTypes,
  CornerRadius,
  HoverEffectVariant,
  HoverIntensity,
  List,
  MaterialIcon,
  useMtwContext,
} from '@makyoapp/makyo-tw-ui'
import { TrailingElement } from '@makyoapp/makyo-tw-ui/dist/components/list/list-card'

import { IViewComponent } from '@/core/app-builder/app/component'
import { parseVariableTextSingleCell } from '@/core/app-builder/parser/variable-text'
import { IDBTable } from '@/core/db'
import { CoreComponentAction } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { isDescendant, parseStringToDescendant } from '@/lib/slate'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { checkVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useComponentNavigation } from '@/hooks/__project__/useComponentNavigation'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { isValidColor, MtColorScheme } from '@/shared/brand-kit'
import { ComponentSettingKey } from '@/shared/builder'
import { LeadingSidebarTab } from '@/shared/builder-ui'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { THUMB_AVATAR } from '@/shared/constants'
import { getSafely } from '@/shared/safely'

import { CellComponent } from '../../shared/CellComponent'
import { getColBrief, getMediaColInfo } from '../../utils'
import { useGetBadgeContent } from './useGetBadgeContent'

export interface BaseCompListProps {
  settings: Record<string, any>
  compId: string
  table: IDBTable
  rowIndexesMap: Map<string, number>
  items: IRow[]
  component: IViewComponent
  mode?: MtColorScheme
  pageBgColor?: ColorVariant
  userData?: Record<string, any>
}

export interface CompListProps extends BaseCompListProps {
  action: CoreComponentAction
  listChecked: any
  setListChecked: (checked: any) => void
  trailingMenuVisible: boolean
  trailingMenuId: string
}

export const CompListCore = ({
  settings,
  compId,
  table,
  rowIndexesMap,
  trailingMenuVisible,
  items,
  mode,
  pageBgColor,
  action: { simulateAction },
  listChecked,
  setListChecked,
  trailingMenuId,
  userData,
  component,
}: CompListProps) => {
  const { currentUser } = useAppAuthContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { activeLayerTableId, activeLayerDetailRowIndex } = useActiveLayerContext()
  const { device } = useBreakpoint()
  const { globalDoc } = useMtwContext()
  const { openComponentDataSettings } = useComponentNavigation()

  const [maxHeight, setMaxHeight] = useState<number>(0)
  const [stack, setStack] = useState(false)

  // ========================= data =========================
  const orientation = settings[ComponentSettingKey.ListDesignOrientation]
  const isHoriz = orientation === 'horizontal'
  const { menus, isDevelopmentMode, setActiveTab, setMenuAction, setMenuItemAction } = useAppBuilderContext()
  const menu = menus.find(({ id }) => id === trailingMenuId)
  const titleCol = getColBrief(table, settings[ComponentSettingKey.ListDataTitle] as string)
  const textCol = getColBrief(table, settings[ComponentSettingKey.ListDataText] as string)
  const text3Col = getColBrief(table, settings[ComponentSettingKey.ListDataText3] as string)
  const badgeCol = getColBrief(table, settings[ComponentSettingKey.ListDataBadge] as string)
  const mediaCol = getColBrief(table, settings[ComponentSettingKey.ListDataMedia] as string)
  const metaCol = getColBrief(table, settings[ComponentSettingKey.ListDataTrailingMetadata] as string)
  const { getBadgeContent } = useGetBadgeContent()
  const shouldHaveSameHeight = settings[ComponentSettingKey.ListDesignSameHeight]
  const [forceSameHeight, setForceSameHeight] = useState(shouldHaveSameHeight)

  const trailingEls = (settings[ComponentSettingKey.ListDataTrailingType] as string[]) ?? []

  const normalizeTrailingType = (s: string) => {
    if (!s) return ''
    const key = s.trim().toLowerCase().replace(/\s+/g, '-')
    const map: Record<string, string> = {
      'multi-select': 'checkbox',
      'toggle-state': 'toggle',
      'action-icon': 'icon-button',
    }
    return map[key] ?? key
  }
  const normalizedTrailingEls = Array.isArray(trailingEls) ? trailingEls.map(normalizeTrailingType) : []

  // ========================= design =========================

  // layout
  const columnsCount = settings[ComponentSettingKey.ListDesignNumCols] ?? 1
  const numRows = (settings[ComponentSettingKey.ListDesignNumRows] as number) ?? 1

  const shape = settings[ComponentSettingKey.ListItemDesignShape] as CornerRadius

  const imageSize = (settings[ComponentSettingKey.ListDesignImageWidth] as string[])?.[0] ?? '56px'

  // typography
  const typoSettings = settings[ComponentSettingKey.ListDesignTypography] as TypographyGroupValue
  const labelTypo = typoSettings[ComponentSettingKey.ListDesignLabelTypography] ?? {
    typography: 'Label Small',
  }
  const supportingTextTypo = typoSettings[ComponentSettingKey.ListDesignSupportingTextTypography] ?? {
    typography: 'Label Small',
  }
  const metaTypo = typoSettings[ComponentSettingKey.ListDesignMetadataTypography] ?? {
    typography: 'Label Small',
  }
  const text3Typo = typoSettings[ComponentSettingKey.ListDesignText3Typography]

  const showBorder = settings[ComponentSettingKey.ListDesignItemShowBorder]

  const listItemBackgroundColor = settings[ComponentSettingKey.ListDesignItemBgColor] ?? 'primary-0'
  const metadataBackgroundColor = settings[ComponentSettingKey.ListDesignMetadataBgColor] ?? 'primary-0'
  const isDynamicColor = Boolean(settings[ComponentSettingKey.ListDesignIconColorCustom])
  const iconColorCol = settings[ComponentSettingKey.ListDesignIconColorColumn] as string
  const iconBackgroundColorCol = settings[ComponentSettingKey.ListDesignIconBackgroundColorColumn] as string

  const iconColorVariant = settings?.[ComponentSettingKey.ListDesignIconColorVariant] ?? ('primary-40' as ColorVariant)
  const iconBackgroundColorVariant =
    settings?.[ComponentSettingKey.ListDesignIconBackgroundColorVariant] ?? ('primary-90' as ColorVariant)
  const iconColorHex = globalDoc?.documentElement
    ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${iconColorVariant}`)
    : undefined
  const iconBackgroundColorHex = globalDoc?.documentElement
    ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${iconBackgroundColorVariant}`)
    : undefined

  const itemBackgroundOpacity = getSafely(settings, [ComponentSettingKey.ListDesignItemBgOpacity], [100])[0] ?? 100
  const opacityFactor = itemBackgroundOpacity / 100

  const hoverEffect = settings[ComponentSettingKey.ListDesignHoverEffect]

  const hoverEffectVariant = settings[ComponentSettingKey.ListDesignHoverEffectVariant] as HoverIntensity

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined
    setMaxHeight(0)

    if (shouldHaveSameHeight) {
      setForceSameHeight((prev: boolean) => !prev)
      timeoutId = setTimeout(() => {
        setForceSameHeight(shouldHaveSameHeight)
      }, 100)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [shouldHaveSameHeight])

  const badgeColor = settings[ComponentSettingKey.ListDesignBadgeColor]

  // SPACING
  // const columnMargin = parseSpacing(settings[ComponentSettingKey.ListDesignColumnMargin] as SpacingInputValue, true)
  const itemPadding = settings[ComponentSettingKey.ListDesignItemPadding] as SpacingInputValue

  const divider = settings[ComponentSettingKey.ListDesignDivider]
  const dividerInset = settings[ComponentSettingKey.ListDesignDividerInset]

  const itemGap = settings[ComponentSettingKey.ListDesignItemGap]
  // const defaultGap = 10
  // const gap = defaultGap + itemGap.value

  // color
  const titleColor = settings[ComponentSettingKey.ListDesignTitleColor] || 'adaptive'
  const titleColorSolid = settings[ComponentSettingKey.ListDesignTitleColorSolid] as ColorVariant
  const titleColorStartGradient = settings[ComponentSettingKey.ListDesignTitleColorStartGradient] as ColorVariant
  const titleColorEndGradient = settings[ComponentSettingKey.ListDesignTitleColorEndGradient] as ColorVariant
  const titleColorGradientDirection = settings[
    ComponentSettingKey.ListDesignTitleColorGradientDirection
  ] as ColorVariant

  const supportingTextColor = settings[ComponentSettingKey.ListDesignSupportingTextColor] || 'adaptive'
  const supportingTextColorSolid = settings[ComponentSettingKey.ListDesignSupportingTextColorSolid] as ColorVariant
  const supportingTextColorStartGradient = settings[
    ComponentSettingKey.ListDesignSupportingTextColorStartGradient
  ] as ColorVariant
  const supportingTextColorEndGradient = settings[
    ComponentSettingKey.ListDesignSupportingTextColorEndGradient
  ] as ColorVariant
  const supportingTextColorGradientDirection = settings[
    ComponentSettingKey.ListDesignSupportingTextColorGradientDirection
  ] as ColorVariant

  const text3Color = settings[ComponentSettingKey.ListDesignText3Color] || 'adaptive'
  const text3ColorSolid = settings[ComponentSettingKey.ListDesignText3ColorSolid] as ColorVariant
  const text3ColorStartGradient = settings[ComponentSettingKey.ListDesignText3ColorStartGradient] as ColorVariant
  const text3ColorEndGradient = settings[ComponentSettingKey.ListDesignText3ColorEndGradient] as ColorVariant
  const text3ColorGradientDirection = settings[
    ComponentSettingKey.ListDesignText3ColorGradientDirection
  ] as ColorVariant

  const colorMode = settings[ComponentSettingKey.ListDesignColorMode] || 'static'
  const colorToneOrder = settings[ComponentSettingKey.ListDesignColorToneOrder] || 'asc'
  const colorStartPalette = settings[ComponentSettingKey.ListDesignColorStartPalette] || 'primary'

  // ========================= actions =========================

  const onItemSelect = settings[ComponentSettingKey.ListActionItemSelection] as IActionRepeaterValue

  // ========================= derived =========================

  const handleOnCheck = (id: string) => {
    setListChecked((maybePrev: any) => {
      openComponentDataSettings(component, 'Item Actions')
      const prev = {
        id: compId,
        items: maybePrev?.id === compId ? [...maybePrev.items] : [],
        icon: 'menu',
        action: onItemSelect,
        extraActionProps: {
          tableId: table.settings._id,
        },
      }
      if (prev.items.includes(id)) {
        prev.items = prev.items.filter((pId) => pId !== id)
      } else {
        prev.items = [...prev.items, id]
      }
      return prev
    })
  }

  const handleIconButtonClick = (rowId: string, rowIndex: number) => {
    const iconButtonClickActions = settings[ComponentSettingKey.ListActionIconButtonClick] as IActionRepeaterValue[]

    openComponentDataSettings(component, 'Item Actions')

    if (!!iconButtonClickActions.length) {
      simulateAction({
        actions: iconButtonClickActions,
        rowId,
        tableId: table.settings._id,
        rowIndex: rowIndexesMap.get(rowId as string) as number,
      })
    }
  }

  const handleItemClick = (rowId: string) => {
    const itemClickActions = settings[ComponentSettingKey.ListActionItemClick] as IActionRepeaterValue[]

    openComponentDataSettings(component, 'Data Source')

    if (isDevelopmentMode) return

    if (!!itemClickActions.length) {
      simulateAction({
        actions: itemClickActions,
        rowId,
        tableId: table.settings._id,
        rowIndex: rowIndexesMap.get(rowId as string) as number,
      })
    }
  }

  const titleLines = settings?.[ComponentSettingKey.ListDesignTitleLines]?.[0] as number
  const supportingTextLines = settings?.[ComponentSettingKey.ListDesignSupportingTextLines]?.[0] as number
  const text3Lines = settings?.[ComponentSettingKey.ListDesignText3Lines]?.[0] as number

  const imgRatio = settings[ComponentSettingKey.ListDesignMediaRatio]
  const imgShape = settings[ComponentSettingKey.ListDesignMediaCorner]

  const renderMenuItems = (row: IRow) => {
    if (!trailingMenuId) return []

    const menu = menus.find(({ id }) => id === trailingMenuId)

    if (!menu) return []

    const isActionButtonsHaveIconOrImage = menu?.items.some((m) => {
      const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
      const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
      const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image
      return isIcon || isAvatar || isImage
    })

    return (menu?.items || [])
      .filter((menuItem) =>
        checkVisibilityV2(
          menuItem.visibility,
          dbClient,
          currentUser?.email || '',
          language,
          userSpecific,
          activeLayerTableId,
          activeLayerDetailRowIndex,
          undefined,
          device
        )
      )
      .map((menuItem) => {
        const isIcon = menuItem?.icon?.flag === VariableSelectFeatureFlag.Icon
        const isAvatar = menuItem?.icon?.flag === VariableSelectFeatureFlag.Avatar
        const isImage = menuItem?.icon?.flag === VariableSelectFeatureFlag.Image

        let media: { icon?: React.ReactNode | undefined; image?: string | undefined } = {
          icon: isActionButtonsHaveIconOrImage ? <div className='w-6 h-6 visible-hidden' /> : undefined,
          image: undefined,
        }

        if (isIcon) {
          media = { icon: <MaterialIcon icon={(menuItem.icon as any)?.value} size={24} /> }
        } else if (isAvatar) {
          media = { image: currentUser?.photo || THUMB_AVATAR, icon: undefined }
        } else if (isImage) {
          media = { image: (menuItem.icon as any)?.value, icon: undefined }
        }

        const badge = getBadgeContent(menuItem)
        let theBadge = undefined

        if (badge.showBadge) {
          if (badge.badgeSize === 's') {
            theBadge = null
          } else {
            theBadge = badge.badgeValue
          }
        }

        // Use the local function to parse the menu item title
        const theLabel = parseVariableTextLocal(menuItem.title)

        return {
          ...media,
          id: menuItem.id,
          label: theLabel,
          badgeText: theBadge,
          type: 'default' as const,
          onClick: () => {
            openComponentDataSettings(component, 'Item Actions')
            setActiveTab(LeadingSidebarTab.Menu)

            setMenuAction({
              flag: ActionFlag.Edit,
              menu: menu,
            })

            setMenuItemAction({
              flag: ActionFlag.Edit,
              menuItemId: menuItem.id,
            })
            if (isDevelopmentMode) return
            if (menuItem.actions && menuItem.actions.length > 0) {
              simulateAction({
                actions: menuItem.actions,
                rowIndex: rowIndexesMap.get(row._id as string) as number,
                tableId: table.settings._id,
                rowId: row._id as string,
              })
            }
          },
        }
      })
  }

  // Local function to parse variable text without using hooks - mimics useVariableTextSingleCell logic
  const parseVariableTextLocal = (value = '') => {
    if (isDescendant(value) && !!dbClient) {
      const node = parseStringToDescendant(value)
      return parseVariableTextSingleCell(node, dbClient, language, userSpecific, currentUser?.email)
    }
    return value
  }

  const selectedTrailingMenu = settings[ComponentSettingKey.ListDataTrailingMenu]
  const isMultipleSelection = settings[ComponentSettingKey.ListDataTrailingToggleCheckBox]
  const isSwitchSelection = settings[ComponentSettingKey.ListDataTrailingToggleSwitch]
  const isIconButtonSelection = settings[ComponentSettingKey.ListDataTrailingToggleIconButton]

  const renderTrailingElements = (row: IRow): TrailingElement[] => {
    let elements: TrailingElement[] = []

    const elementOrder =
      normalizedTrailingEls.length > 0
        ? normalizedTrailingEls
        : ['menu', 'checkbox', 'toggle', 'icon-button', 'metadata']

    const addElementIfCondition = (condition: boolean, elementFactory: () => TrailingElement) => {
      if (condition) {
        elements.push(elementFactory())
      }
    }

    for (const type of elementOrder) {
      switch (type) {
        case 'menu':
          addElementIfCondition(selectedTrailingMenu && trailingMenuVisible && (menu?.items || [])?.length > 0, () => ({
            id: 'menu',
            type: 'menu',
            menuType: 'profile',
            items: renderMenuItems(row),
          }))
          break

        case 'checkbox':
          addElementIfCondition(isMultipleSelection, () => {
            const isChecked = listChecked?.id === compId && listChecked?.items.includes(row._id as string)
            return {
              id: 'checkbox',
              type: 'checkbox',
              isChecked,
              onCheck: () => handleOnCheck(row._id as string),
            }
          })
          break

        case 'toggle':
          addElementIfCondition(isSwitchSelection, () => {
            const toggleColId = settings[ComponentSettingKey.ListDataTrailingToggle] as string
            const isToggleUserSpec = settings[ComponentSettingKey.ListDataTrailingToggleUserSpec] as boolean
            const selected =
              (!toggleColId
                ? false
                : isToggleUserSpec
                  ? userData
                    ? (userData[toggleColId] as boolean)
                    : (row[toggleColId]?.value as boolean)
                  : (row[toggleColId]?.value as boolean)) ?? false
            return {
              id: 'switch',
              type: 'switch',
              activeIcon: 'favorite',
              inactiveIcon: 'favorite_border',
              isActive: selected,
              onToggle: () => {
                // handleToggleClick(row._id as string, rowIndexesMap.get(row._id as string) as number)
              },
            }
          })
          break

        case 'icon-button':
          addElementIfCondition(isIconButtonSelection, () => {
            const icon = (settings?.[ComponentSettingKey.ListDataTrailingIconButtonIcon] as string) || 'chevron_right'
            return {
              id: 'iconButton',
              type: 'iconButton',
              icon: icon,
              ariaLabel: 'iconButton',
              iconFill: false,
              iconSize: 24,
              onClick: () => handleIconButtonClick(row._id as string, rowIndexesMap.get(row._id as string) as number),
            }
          })
          break
      }
    }

    if (metaCol) {
      addElementIfCondition(!!metaCol, () => {
        const cell = row[metaCol?._id as string]
        const metaColValue = cell?.value
        const metaColValString = cell?.toString()
        const isRating = metaCol?._Type === ColumnTypes.Rating

        if (isRating) {
          const ratingValue = metaColValue
          const ratingMax = cell?.settings?.ratingMax || ratingValue
          return {
            id: 'rating',
            type: 'rating',
            maxValue: ratingMax,
            value: parseInt((ratingValue || 0) as string),
          }
        } else {
          const isValueEmpty = !metaColValString || metaColValString.trim() === ''
          return {
            id: 'metadata',
            type: 'metadata',
            value: !isValueEmpty ? (
              <CellComponent
                settings={metaCol?._Settings}
                fileName={metaColValString}
                cell={cell}
                type={metaCol?._Type as ColumnTypes}
                mode={mode}
                backgroundColor={listItemBackgroundColor}
              />
            ) : (
              0
            ),
          }
        }
      })
    }

    return elements
  }
  // NOTE: comment old code
  // const renderTrailingElements = (row: IRow): TrailingElement[] => {
  //   let elements: TrailingElement[] = []

  //   // if (selectedTrailingMenu && trailingMenuVisible && (menu?.items || [])?.length > 0) {
  //   //   elements.push({
  //   //     id: 'menu',
  //   //     type: 'menu',
  //   //     menuType: 'profile',
  //   //     items: renderMenuItems(row),
  //   //   })
  //   // }

  //   // if (isMultipleSelection) {
  //   //   const isChecked = listChecked?.id === compId && listChecked?.items.includes(row._id as string)

  //   //   elements.push({
  //   //     id: 'checkbox',
  //   //     type: 'checkbox',
  //   //     isChecked,
  //   //     onCheck: () => {
  //   //       handleOnCheck(row._id as string)
  //   //     },
  //   //   })
  //   // }

  //   // if (isSwitchSelection) {
  //   //   const toggleColId = settings[ComponentSettingKey.ListDataTrailingToggle] as string
  //   //   const isToggleUserSpec = settings[ComponentSettingKey.ListDataTrailingToggleUserSpec] as boolean
  //   //   const selected =
  //   //     (!toggleColId
  //   //       ? false
  //   //       : isToggleUserSpec
  //   //         ? userData
  //   //           ? (userData[toggleColId] as boolean)
  //   //           : (row[toggleColId]?.value as boolean)
  //   //         : (row[toggleColId]?.value as boolean)) ?? false
  //   //   elements.push({
  //   //     id: 'switch',
  //   //     type: 'switch',
  //   //     activeIcon: 'favorite',
  //   //     inactiveIcon: 'favorite_border',
  //   //     isActive: selected,
  //   //     onToggle: () => {
  //   //       // handleToggleClick(row._id as string, rowIndexesMap.get(row._id as string) as number)
  //   //     },
  //   //   })
  //   // }

  //   // if (isIconButtonSelection) {
  //   //   const icon = (settings?.[ComponentSettingKey.ListDataTrailingIconButtonIcon] as string) || 'chevron_right'

  //   //   elements.push({
  //   //     id: 'iconButton',
  //   //     type: 'iconButton',
  //   //     icon: icon,
  //   //     ariaLabel: 'iconButton',
  //   //     iconFill: false,
  //   //     iconSize: 24,
  //   //     onClick: () => {
  //   //       handleIconButtonClick(row._id as string, rowIndexesMap.get(row._id as string) as number)
  //   //     },
  //   //   })
  //   // }

  //   // Iterate through trailingEls to preserve order
  //   // for (const type of trailingEls) {
  //   //   if (type === 'menu' && trailingMenuVisible && (menu?.items || [])?.length > 0) {
  //   //     elements.push({
  //   //       id: 'menu',
  //   //       type: 'menu',
  //   //       menuType: 'profile',
  //   //       items: renderMenuItems(row),
  //   //     })
  //   //   }

  //   //   if (type === 'metadata') {
  //   //     const cell = row[metaCol?._id as string]
  //   //     const metaColValue = cell?.value
  //   //     const metaColValString = cell?.toString()
  //   //     const isRating = metaCol?._Type === ColumnTypes.Rating

  //   //     if (isRating) {
  //   //       const ratingValue = metaColValue
  //   //       const ratingMax = cell?.settings?.ratingMax || ratingValue

  //   //       elements.push({
  //   //         id: 'rating',
  //   //         type: 'rating',
  //   //         maxValue: ratingMax,
  //   //         value: parseInt((ratingValue || 0) as string),
  //   //       })
  //   //     }
  //   //     // todo: no need to handle datetime, should have handled icon instead
  //   //     // else if (isDatetime) {
  //   //     //   elements.push({
  //   //     //     id: 'datetime',
  //   //     //     type: 'metadata',
  //   //     //     value: String(cell.value),
  //   //     //   })
  //   //     // }
  //   //     else {
  //   //       const isValueEmpty = !metaColValString || metaColValString.trim() === ''

  //   //       elements.push({
  //   //         id: 'metadata',
  //   //         type: 'metadata',
  //   //         value: !isValueEmpty ? (
  //   //           <CellComponent
  //   //             settings={metaCol?._Settings}
  //   //             fileName={metaColValString}
  //   //             cell={cell}
  //   //             type={metaCol?._Type as ColumnTypes}
  //   //             mode={mode}
  //   //             backgroundColor={listItemBackgroundColor}
  //   //           />
  //   //         ) : (
  //   //           0
  //   //         ),
  //   //       })
  //   //     }
  //   //   }

  //   //   if (type === 'checkbox') {
  //   //     const isChecked = listChecked?.id === compId && listChecked?.items.includes(row._id as string)

  //   //     elements.push({
  //   //       id: 'checkbox',
  //   //       type: 'checkbox',
  //   //       isChecked,
  //   //       onCheck: () => {
  //   //         handleOnCheck(row._id as string)
  //   //       },
  //   //     })
  //   //   }

  //   //   if (type === 'icon-button') {
  //   //     const icon = (settings?.[ComponentSettingKey.ListDataTrailingIconButtonIcon] as string) || 'chevron_right'

  //   //     elements.push({
  //   //       id: 'iconButton',
  //   //       type: 'iconButton',
  //   //       icon: icon,
  //   //       ariaLabel: 'iconButton',
  //   //       iconFill: false,
  //   //       iconSize: 24,
  //   //       onClick: () => {
  //   //         handleIconButtonClick(row._id as string, rowIndexesMap.get(row._id as string) as number)
  //   //       },
  //   //     })
  //   //   }

  //   //   if (type === 'toggle') {
  //   //     const toggleColId = settings[ComponentSettingKey.ListDataTrailingToggle] as string
  //   //     const isToggleUserSpec = settings[ComponentSettingKey.ListDataTrailingToggleUserSpec] as boolean
  //   //     const selected =
  //   //       (!toggleColId
  //   //         ? false
  //   //         : isToggleUserSpec
  //   //           ? userData
  //   //             ? (userData[toggleColId] as boolean)
  //   //             : (row[toggleColId]?.value as boolean)
  //   //           : (row[toggleColId]?.value as boolean)) ?? false
  //   //     elements.push({
  //   //       id: 'switch',
  //   //       type: 'switch',
  //   //       activeIcon: 'favorite',
  //   //       inactiveIcon: 'favorite_border',
  //   //       isActive: selected,
  //   //       onToggle: () => {
  //   //         // handleToggleClick(row._id as string, rowIndexesMap.get(row._id as string) as number)
  //   //       },
  //   //     })
  //   //   }
  //   // }
  //   return elements
  // }

  const getFinalIconColor = (row: IRow) => {
    if (isDynamicColor && iconColorCol) {
      const color = row[iconColorCol]?.toString()
      return isValidColor(color) ? color : iconColorHex
    }
    return iconColorHex
  }

  const getFinalIconBackgroundColor = (row: IRow) => {
    if (isDynamicColor && iconBackgroundColorCol) {
      const color = row[iconBackgroundColorCol]?.toString()
      return isValidColor(color) ? color : iconBackgroundColorHex
    }
    return iconBackgroundColorHex
  }

  const getListItemProps = (row: IRow) => {
    const [mediaValue, mediaType] = getMediaColInfo(row, mediaCol)

    const iconSize = (settings?.[ComponentSettingKey.ListDesignIconSize] as number[])?.[0]
    const parsedImageSize = typeof imageSize === 'string' ? Number(imageSize.replace(/[^0-9.]/g, '')) || 56 : imageSize
    const iconVariant = getSafely(settings, [ComponentSettingKey.ListDesignIconVariant], 'standard') as
      | 'standard'
      | 'filled'
      | 'outlined'

    const badgeText = badgeCol && badgeCol._id ? row[badgeCol._id]?.toString() : ''

    const finalIconColor = getFinalIconColor(row)
    const finalIconBackgroundColor = getFinalIconBackgroundColor(row)

    const isValidImageUrl = mediaType === 'image' || !!(mediaValue && isValidUrl(mediaValue))

    return {
      id: row._id as string,
      title: titleCol && titleCol._id ? row[titleCol._id]?.toString() : '',
      supportingText: textCol && textCol._id ? row[textCol._id]?.toString() : '',
      text3: text3Col && text3Col._id ? row[text3Col._id]?.toString() : '',
      badgeText,
      useImage: isValidImageUrl,
      imageUrl: isValidImageUrl ? mediaValue : undefined,
      iconFill: false,
      iconSize,
      backgroundColor: listItemBackgroundColor as ColorVariant,
      backgroundColorPage: pageBgColor as ColorVariant,
      metadataBackgroundColor: metadataBackgroundColor as ColorVariant,
      iconName: mediaType === 'icon' ? mediaValue : undefined,
      iconColor: finalIconColor,
      iconBackgroundColor:
        isDynamicColor && finalIconBackgroundColor ? finalIconBackgroundColor : iconBackgroundColorVariant,
      isIconDynamicColor: isDynamicColor,
      titleTypography: labelTypo,
      supportingTextTypography: supportingTextTypo,
      metadataTypography: metaTypo,
      text3Typography: text3Typo,
      trailingElements: renderTrailingElements(row),
      iconType: iconVariant,
      imageWidth: parsedImageSize,
      backgroundTransparency: itemBackgroundOpacity,
      maxTitleLine: titleLines,
      maxSupportingTextLine: supportingTextLines,
      maxText3Line: text3Lines,
      imageBorderRadius: imgShape,
      cardBorderRadius: shape,
      showHover: false, // hoverEffect,
      titleColorType: titleColor,
      titleColorSolid: titleColorSolid || 'primary-40',
      titleColorStartGradient: titleColorStartGradient || 'primary-40',
      titleColorEndGradient: titleColorEndGradient || 'primary-40',
      titleColorGradientDirection: titleColorGradientDirection,
      supportingTextColorType: supportingTextColor,
      supportingTextColorSolid: supportingTextColorSolid || 'primary-40',
      supportingTextColorStartGradient: supportingTextColorStartGradient || 'primary-40',
      supportingTextColorEndGradient: supportingTextColorEndGradient || 'primary-40',
      supportingTextColorGradientDirection: supportingTextColorGradientDirection,
      text3ColorType: text3Color,
      text3ColorSolid: text3ColorSolid || 'primary-40',
      text3ColorStartGradient: text3ColorStartGradient || 'primary-40',
      text3ColorEndGradient: text3ColorEndGradient || 'primary-40',
      text3ColorGradientDirection: text3ColorGradientDirection,
      hoverEffect: hoverEffectVariant as HoverEffectVariant,
      hoverIntensity: 'md' as HoverIntensity,
      ...(imgRatio ? { imageAspectRatio: imgRatio } : {}),
    }
  }

  const handleTrailingElementOpenChange = () => {
    openComponentDataSettings(component, 'Item Actions')
  }

  const renderItems = (itemRows: IRow[]) => {
    return (
      <List
        className={cn(isDevelopmentMode && 'border border-dashed !border-gray-800')}
        key={`${compId}-${numRows}-${columnsCount}-${forceSameHeight}-${stack}`}
        id={compId}
        rows={numRows}
        showBorder={showBorder}
        onItemClick={handleItemClick}
        onTrailingElementOpenChange={handleTrailingElementOpenChange}
        itemShape={shape}
        // onLayoutChange={() => {
        //   setStack(false)
        // }}
        backgroundColor={`primary-100`}
        backgroundColorPage={pageBgColor}
        itemOpacity={itemBackgroundOpacity / 100}
        gap={itemGap?.value}
        gapUnit={itemGap?.unit}
        style={{
          overflow: 'hidden',
          cursor: hoverEffect ? 'pointer' : 'default',
        }}
        maxHeightPerItem={forceSameHeight ? maxHeight : undefined}
        stacked={false}
        onMaxHeightChange={(height) => {
          if (maxHeight < height) {
            setMaxHeight(height)
          }
        }}
        // onStackedChange={(stack) => {
        //   setStack(stack)
        // }}
        columns={columnsCount}
        itemPadding={itemPadding}
        forceSameHeight={forceSameHeight}
        itemSpacing={{
          padding: {
            top: itemPadding.top?.value,
            bottom: itemPadding.bottom?.value,
            left: itemPadding.left?.value,
            right: itemPadding.right?.value,
          },
        }}
        orientation={'vertical'}
        items={itemRows.map(getListItemProps)}
        showDivider={divider}
        dividerInset={dividerInset}
        badgeColor={badgeColor}
        colorGeneration={{
          mode: colorMode,
          seed: `${'seed-list'}-${compId}`,
          startIndex: 0,
          excludeColors: [],
          toneOrder: colorToneOrder,
          startPalette: colorStartPalette,
        }}
      />
    )
  }

  const renderItemsHorizontal = (itemRows: IRow[]) => {
    const sizeToSplit = numRows * columnsCount
    const chunks: IRow[][] = []
    for (let i = 0; i < itemRows.length; i += sizeToSplit) {
      chunks.push(itemRows.slice(i, i + sizeToSplit))
    }

    const getWidth = (chunk: IRow[], index: number) => {
      if (chunks.length === 1) return '100%'

      // For partial chunks, scale the width proportionally to maintain item sizes
      if (chunk.length < columnsCount) {
        // Calculate how many columns this chunk actually needs
        const neededColumns = Math.ceil(chunk.length) / columnsCount
        // Scale the 95% width by the ratio of needed columns to total columns
        return `${neededColumns * 95}%`
      }

      return '95%'
    }

    return (
      <div
        className='flex flex-row overflow-x-auto max-w-full'
        style={{
          gap: `${itemGap?.value}px`,
        }}
      >
        {chunks.map((chunk, index) => (
          <div key={`${index}-${forceSameHeight}`} style={{ width: getWidth(chunk, index) }} className='shrink-0'>
            <List
              showBorder={showBorder}
              widthAmount={100}
              // onLayoutChange={() => {
              //   setStack(false)
              // }}
              className='shrink-0'
              widthUnit={'percentage'}
              id={compId}
              maxHeightPerItem={forceSameHeight ? maxHeight : undefined}
              stacked={false}
              onMaxHeightChange={(height) => {
                if (maxHeight < height) {
                  setMaxHeight(height)
                }
              }}
              // onStackedChange={(stack) => {
              //   setStack(stack)
              // }}
              rows={Math.min(Math.max(numRows, 1), chunks.length)}
              onItemClick={handleItemClick}
              onTrailingElementOpenChange={handleTrailingElementOpenChange}
              itemShape={shape}
              backgroundColor={`primary-100`}
              backgroundColorPage={pageBgColor}
              itemOpacity={itemBackgroundOpacity / 100}
              gap={itemGap?.value}
              gapUnit={itemGap?.unit}
              style={{
                overflow: 'hidden',
                cursor: hoverEffect ? 'pointer' : 'default',
              }}
              columns={columnsCount}
              itemPadding={itemPadding}
              forceSameHeight={shouldHaveSameHeight}
              itemSpacing={{
                padding: {
                  top: itemPadding.top?.value,
                  bottom: itemPadding.bottom?.value,
                  left: itemPadding.left?.value,
                  right: itemPadding.right?.value,
                },
              }}
              orientation={'horizontal'}
              items={chunk.map(getListItemProps)}
              badgeColor={badgeColor}
              colorGeneration={{
                mode: colorMode,
                seed: `${'seed-list'}-${compId}`,
                startIndex: 0,
                excludeColors: [],
                toneOrder: colorToneOrder,
                startPalette: colorStartPalette,
              }}
            />
          </div>
        ))}
      </div>
    )
  }

  return isHoriz ? <div className='w-full shrink-0'>{renderItemsHorizontal(items)}</div> : <>{renderItems(items)}</>
}
